// Client popup requesting push notification permission.

import type { BrowserPushPermissionState } from 'open-modules/zdf/component/notifications/push-notification';
import {
    AskForPermissionScreen,
    PermissionPopup,
    usePushNotificationPermission,
} from 'open-modules/zdf/component/notifications/push-notification';
import { useEffect, useRef, useState } from 'react';
import { callServerApiAsync } from 'system/servercall';
import { useDatastore, usePersonaKey } from 'util/datastore';
import { makeUrl } from 'util/navigate';

import {
    arePushNotificationsHandledOnHostSite,
    fetchBrowserPushNotificationTokenClientAsync,
    getUserHasHandledPushNotificationDialog,
    registerBrowserPushNotificationServiceWorkerAsync,
    updateServiceWorkerRegistrationAndFetchTokenAsync,
} from './pushNotificationUtils.web';

export function requestBrowserPushNotificationPopupWindowHandler({
    onFinish = (permission?: BrowserPushPermissionState) => {},
    onStart = () => {},
    onCancel = () => {},
}) {
    const openAskForPermissionWindow = () => {
        const openedWindow = window.open(
            makeUrl(['account', 'one', 'browserPermission']),
            'PopupWindow',
            'width=500,height=300,toolbar=no,menubar=no,scrollbars=no,resizable=no',
        );

        if (openedWindow) {
            onStart && onStart();
            const pollTimer = setInterval(() => {
                if (openedWindow.closed) {
                    clearInterval(pollTimer);
                    onFinish && onFinish(Notification.permission);
                }
            }, 1000);
        } else {
            onCancel && onCancel();
            console.warn('Popup was blocked');
        }
    };

    return {
        requestPermission: openAskForPermissionWindow,
    };
}

export function requestBrowserPushNotificationClientHandler({
    datastore,
    onFinish = (permission?: BrowserPushPermissionState) => {},
    onStart = () => {},
    onCancel = () => {},
    onError = () => {},
}) {
    const enablePermissionAsync = async () => {
        onStart?.();
        let token;
        let registration;
        try {
            registration = await registerBrowserPushNotificationServiceWorkerAsync();
        } catch {
            onError?.();
            return;
        }

        try {
            token = await fetchBrowserPushNotificationTokenClientAsync(registration);
            if (token) {
                await callServerApiAsync({
                    datastore,
                    component: 'pushNotifications',
                    funcname: 'savePushToken',
                    params: {
                        token: token,
                    },
                });
            }
            onFinish?.(Notification.permission);
        } catch {
            onFinish?.(Notification.permission);
        }
    };

    return {
        requestPermission: enablePermissionAsync,
    };
}

export function PermissionPopupClientIframe({ onAllow, onNotAllow }) {
    const [permission, setPermission] = useState(Notification.permission);
    const [hasOptedOut, setHasOptedOut] = useState(getUserHasHandledPushNotificationDialog());
    const [hidePopup, setHidePopup] = useState(false);
    const pushNotificationPermissionStatus = usePushNotificationPermission();

    const hideModal = (permission === 'granted' && pushNotificationPermissionStatus?.status) || hasOptedOut || hidePopup;

    const updatePermissionData = () => {
        setPermission(Notification.permission);
        setHasOptedOut(getUserHasHandledPushNotificationDialog());
    };

    const { requestPermission } = requestBrowserPushNotificationPopupWindowHandler({
        onFinish: updatePermissionData,
        onCancel: updatePermissionData,
        onStart: () => {
            setHidePopup(true);
            onAllow && onAllow();
        },
    });

    const handleAllow = () => {
        requestPermission();
    };

    const handleNotAllow = () => {
        localStorage.setItem('doNotShowPushNotificationDialogAgain', 'true');
        updatePermissionData();
        onNotAllow?.();
    };

    if (hideModal) return null;

    return <PermissionPopup handleAllow={handleAllow} handleNotAllow={handleNotAllow} permission={'default'} />;
}

export function AskForPermissionScreenPopup() {
    const datastore = useDatastore();
    const [permission, setPermission] = useState(Notification.permission);
    const permissionInitial = useRef(Notification.permission);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const enablePermissionAsync = async () => {
        if (permission === 'default') {
            setLoading(true);
            let token;
            let registration;
            try {
                registration = await registerBrowserPushNotificationServiceWorkerAsync();
            } catch {
                setError(true);
                setPermission(Notification.permission);
                setLoading(false);
                return;
            }

            try {
                token = await fetchBrowserPushNotificationTokenClientAsync(registration);
                if (token) {
                    await callServerApiAsync({
                        datastore,
                        component: 'pushNotifications',
                        funcname: 'savePushToken',
                        params: {
                            token: token,
                        },
                    });
                }
                setLoading(false);
                setPermission(Notification.permission);
            } catch {
                setLoading(false);
                setPermission(Notification.permission);
            }
        }
    };

    return (
        <AskForPermissionScreen
            already={permissionInitial.current === 'granted'}
            error={error}
            loading={loading}
            onRequest={enablePermissionAsync}
            permission={permission}
        />
    );
}

export function PermissionPopupClient({ onAllow, onNotAllow }) {
    const datastore = useDatastore();
    const [permission, setPermission] = useState(Notification.permission);
    const [hasOptedOut, setHasOptedOut] = useState(getUserHasHandledPushNotificationDialog());
    const [loading, setLoading] = useState(false);

    const hideModal = permission === 'granted' || hasOptedOut;

    const updatePermissionData = () => {
        setPermission(Notification.permission);
        setHasOptedOut(getUserHasHandledPushNotificationDialog());
    };

    const { requestPermission } = requestBrowserPushNotificationClientHandler({
        datastore,
        onFinish: () => {
            updatePermissionData();
            setLoading(false);
        },
        onCancel: () => {
            updatePermissionData();
            setLoading(false);
        },
        onStart: () => {
            setLoading(true);
            onAllow?.();
        },
    });

    const handleAllow = () => {
        requestPermission();
    };

    const handleNotAllow = () => {
        localStorage.setItem('doNotShowPushNotificationDialogAgain', 'true');
        updatePermissionData();
        onNotAllow?.();
    };

    if (hideModal) return null;

    return <PermissionPopup handleAllow={handleAllow} handleNotAllow={handleNotAllow} loading={loading} permission={permission} />;
}
